import { FC } from 'react';
import { ChevronLeft } from '@ixigo/icons';
import Typography from '@ixigo/iui/Typography';
import { Organisation } from '../../enums/common';
import contactSupport from '../../images/contactSupport.svg';
import { formatDate, getOrganisation } from '../../utils/common';

interface BookingDetails {
    hotelInfo?: {
        name?: string;
    };
    origin?: string;
    originCode?: string;
    destination?: string;
    destinationCode?: string;
    hotelName?: string;
    date?: string;
    createdAt?: string;
    product?: string;
}

interface BookingHistoryHeaderProps {
    bookingDetails: BookingDetails;
    onBack: () => void;
    onContactSupport: () => void;
    showEscalation?: boolean;
}

const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
};

const getDisplayDate = (bookingDetails: BookingDetails) => {
    if (bookingDetails.date) return formatDate(bookingDetails.date);
    if (bookingDetails.createdAt) return formatDate(bookingDetails.createdAt);
    return '';
};

const BookingHistoryHeader: FC<BookingHistoryHeaderProps> = ({
    bookingDetails,
    onBack,
    onContactSupport,
    showEscalation = false,
}) => {
    const org = getOrganisation();
    const isAbhibus = org === Organisation.ABHIBUS;

    const renderBookingInfo = () => {
        if (bookingDetails.hotelInfo?.name) {
            return truncateText(bookingDetails.hotelInfo?.name, 25);
        }

        // For Abhibus, use origin and destination fields
        if (isAbhibus && bookingDetails.origin && bookingDetails.destination) {
            return `${truncateText(bookingDetails.origin, 15)} - ${truncateText(bookingDetails.destination, 15)}`;
        }

        // For other products, use originCode and destinationCode
        if (bookingDetails.originCode && bookingDetails.destinationCode) {
            return `${bookingDetails.originCode} - ${bookingDetails.destinationCode}`;
        }

        return '';
    };

    return (
        <div className='bg-white px-15 py-10 flex items-center justify-between border-b border-charcoal-100'>
            {!isAbhibus && (
                <button onClick={onBack} className='p-5' type='button' aria-label='Go back'>
                    <ChevronLeft className='text-charcoal-800' width={24} height={24} />
                </button>
            )}
            <div className='flex flex-col items-center flex-1'>
                <Typography variant='h6' className='text-charcoal-800'>
                    Past Tickets
                </Typography>
                <Typography variant='body-sm' className='text-charcoal-600'>
                    {renderBookingInfo()} • {getDisplayDate(bookingDetails)}
                </Typography>
            </div>
            <div className='flex flex-col items-center'>
                <button
                    onClick={onContactSupport}
                    className={`p-5 hover:bg-charcoal-50 rounded-full transition-colors ${
                        !showEscalation ? 'invisible' : ''
                    }`}
                    type='button'
                    aria-label='Contact Support'
                >
                    <img src={contactSupport} alt='Contact Support' width={24} height={24} />
                </button>
            </div>
        </div>
    );
};

export default BookingHistoryHeader;
